import { defineStore } from 'pinia'
import { getContentReviewProfiles } from '@/content-review/api'
import { CUSTOMIZATIONS, type CustomizationKey } from './customization_demo_catalog'
import {
  readStoredSelections,
  writeStoredSelections,
  type DemoSelections
} from './customization_demo_storage'

type AvailableProfileIds = Partial<Record<CustomizationKey, string[]>>

// Auto-review is the customization this SPA delivers, so it is the one this store can start: the
// content review store's `reviewProfileId` reads the selection live, so choosing a category takes
// effect on the page with no refetch. Goal customizations are delivered by toolkit-spa; this store
// only reports and clears those, and the shared session storage is what carries a selection
// between the two SPAs.
export const useCustomizationDemoStore = defineStore('customizationDemo', {
  state: () => ({
    selectionByCustomization: readStoredSelections() as DemoSelections,
    availableProfileIdsByCustomization: {} as AvailableProfileIds,
    fetching: false,
    error: null as unknown
  }),
  actions: {
    // A category whose profile carries no rubric would review against Goalbook's default criteria
    // and look like nothing happened, so the picker checks up front and disables those options.
    // Only auto-review has a backend this SPA asks; goal customizations are left unchecked here,
    // which `isProfileAvailable` reads as available.
    async fetchAvailability() {
      this.fetching = true
      this.error = null
      try {
        const profiles = await getContentReviewProfiles()
        const available: AvailableProfileIds = {
          [CUSTOMIZATIONS.AUTO_REVIEW]: profiles
            .map((profile) => profile.content_profile)
            .filter(Boolean)
        }
        this.availableProfileIdsByCustomization = available
        this.dropStaleSelections(available)
      } catch (error) {
        this.error = error
      } finally {
        this.fetching = false
      }
    },
    // A selection restored from storage can name a profile the backend no longer reports — a
    // rubric unreleased in Airtable, or a refreshed cache. Disabling the option is not enough: the
    // selection would still be sent as content_profile. Drop those, leaving customizations the
    // backend said nothing about untouched.
    dropStaleSelections(available: AvailableProfileIds) {
      const stale = (Object.keys(available) as CustomizationKey[]).filter((customization) => {
        const selected = this.selectionByCustomization[customization]
        return !!selected && !available[customization]?.includes(selected)
      })
      if (!stale.length) return

      const next: DemoSelections = { ...this.selectionByCustomization }
      stale.forEach((customization) => {
        next[customization] = null
      })
      this.selectionByCustomization = next
      writeStoredSelections(next)
    },
    selectProfile(customization: CustomizationKey, profileId: string) {
      this.selectionByCustomization = {
        ...this.selectionByCustomization,
        [customization]: profileId || null
      }
      writeStoredSelections(this.selectionByCustomization)
    },
    turnOff(customization: CustomizationKey) {
      this.selectProfile(customization, '')
    },
    turnOffAll() {
      this.selectionByCustomization = {}
      writeStoredSelections(this.selectionByCustomization)
    }
  },
  getters: {
    demoProfileFor(state) {
      return (customization: CustomizationKey): string =>
        state.selectionByCustomization[customization] || ''
    },
    isProfileAvailable(state) {
      return (customization: CustomizationKey, profileId: string): boolean => {
        const checked = state.availableProfileIdsByCustomization[customization]
        return checked ? checked.includes(profileId) : true
      }
    },
    activeDemoCount(state): number {
      return Object.values(state.selectionByCustomization).filter(Boolean).length
    },
    isDemoActive(): boolean {
      return this.activeDemoCount > 0
    }
  }
})
